import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Role } from '../api/types';
import { ApiClient, apiClient as sharedApiClient, DEMO_TENANT_ID } from '../api/client';
import { DEMO_USERS } from './config';
import {
  DEFAULT_ROLE_VIEW,
  isRoleViewKey,
  type RoleViewKey,
} from '../components/roleViews';

/** Authenticated staff user (demo projection of the production claims set). */
export interface StaffUser {
  userId: string;
  name: string;
  email: string;
  role: Role;
  tenantId: string;
}

export interface AuthContextValue {
  user: StaffUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  apiClient: ApiClient;
  /** Active tenant (hospital) id — may differ from the user's home tenant
   *  when the operator switches hospital context in the top bar (FND-003). */
  tenantId: string;
  /** Switch the active hospital/tenant context (persisted). Drives the
   *  `X-Tenant-Id` header on every request via the shared apiClient. */
  setTenant: (tenantId: string) => void;
  /** Active role label — may be re-scoped via the top-bar role switcher. */
  activeRole: Role;
  /** Re-scope the active role (presentation/queue scoping; persisted). */
  setActiveRole: (role: Role) => void;
  /**
   * Active UI scoping persona (the role-first "view"). Decoupled from the auth
   * `Role`: it trims the nav and redirects to a landing page but never changes
   * the bearer token or tenant, so a scoped view (e.g. `cfo`) still calls the
   * API as the logged-in staff user. Defaults to `all` (full client tour).
   */
  activeView: RoleViewKey;
  /** Switch the active UI scoping persona (persisted). */
  setActiveView: (view: RoleViewKey) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'enable2_staff_token';
const USER_KEY = 'enable2_staff_user';
const TENANT_KEY = 'enable2_staff_tenant';
const ROLE_KEY = 'enable2_staff_role';
const VIEW_KEY = 'enable2_staff_view';

function loadUser(): StaffUser | null {
  const stored = localStorage.getItem(USER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as StaffUser;
  } catch {
    return null;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY)
  );
  const [user, setUser] = useState<StaffUser | null>(() => loadUser());
  const [isLoading, setIsLoading] = useState(false);
  // Active hospital/role context — defaults to the logged-in user's, but the
  // operator can override them in the top bar (hospital + role switchers).
  const [tenantOverride, setTenantOverride] = useState<string | null>(() =>
    localStorage.getItem(TENANT_KEY)
  );
  const [roleOverride, setRoleOverride] = useState<Role | null>(
    () => (localStorage.getItem(ROLE_KEY) as Role | null) || null
  );
  // UI scoping persona (role-first "view"). Defaults to `all` (full tour).
  const [activeView, setActiveViewState] = useState<RoleViewKey>(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return isRoleViewKey(stored) ? stored : DEFAULT_ROLE_VIEW;
  });

  const tenantId = tenantOverride ?? user?.tenantId ?? DEMO_TENANT_ID;
  const activeRole: Role = roleOverride ?? user?.role ?? 'staff_admin';

  // Keep the shared `apiClient` singleton (used by every data hook) in lock-step
  // with auth state. Done during render — child hooks fire their fetch on mount
  // and React runs effects children-first, so setting this in an effect here
  // would be too late for the first request and yield "Missing authentication
  // token" 401s on the initial page load.
  sharedApiClient.setToken(token);
  sharedApiClient.setTenant(tenantId);

  const apiClient = useMemo(() => {
    const client = new ApiClient(tenantId);
    client.setToken(token);
    return client;
  }, [token, tenantId]);

  const setTenant = useCallback((next: string) => {
    setTenantOverride(next);
    localStorage.setItem(TENANT_KEY, next);
  }, []);

  const setActiveRole = useCallback((next: Role) => {
    setRoleOverride(next);
    localStorage.setItem(ROLE_KEY, next);
  }, []);

  const setActiveView = useCallback((next: RoleViewKey) => {
    setActiveViewState(next);
    localStorage.setItem(VIEW_KEY, next);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    try {
      // Demo login: validate against seeded staff accounts. Production swaps
      // this for an Entra ID redirect flow (see auth/config.ts).
      const match = DEMO_USERS.find(
        (u) => u.username === username && u.password === password
      );
      if (!match) {
        throw new Error('Invalid username or password');
      }
      const staffUser: StaffUser = {
        userId: match.userId,
        name: match.name,
        email: match.email,
        role: match.role,
        tenantId: match.tenantId,
      };
      // Opaque demo token; the mock backend treats any bearer token as valid.
      const demoToken = `demo.${match.userId}.${match.role}`;
      setToken(demoToken);
      setUser(staffUser);
      // A fresh login resets any prior hospital/role context overrides and
      // returns to the default `all` view (full client tour).
      setTenantOverride(null);
      setRoleOverride(null);
      setActiveViewState(DEFAULT_ROLE_VIEW);
      localStorage.removeItem(TENANT_KEY);
      localStorage.removeItem(ROLE_KEY);
      localStorage.removeItem(VIEW_KEY);
      localStorage.setItem(TOKEN_KEY, demoToken);
      localStorage.setItem(USER_KEY, JSON.stringify(staffUser));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setTenantOverride(null);
    setRoleOverride(null);
    setActiveViewState(DEFAULT_ROLE_VIEW);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TENANT_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(VIEW_KEY);
  }, []);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!token && !!user,
      isLoading,
      login,
      logout,
      apiClient,
      tenantId,
      setTenant,
      activeRole,
      setActiveRole,
      activeView,
      setActiveView,
    }),
    [
      user,
      token,
      isLoading,
      login,
      logout,
      apiClient,
      tenantId,
      setTenant,
      activeRole,
      setActiveRole,
      activeView,
      setActiveView,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
